import type { Router } from 'vue-router';

import { sentryModel } from '@/shared/model';

/**
 * Route pages are loaded via dynamic `import()` (route-level code splitting),
 * so their JS/CSS chunks carry content-hashed filenames. A chunk load can fail
 * on a flaky mobile-webview connection that drops a request mid-flight.
 *
 * For now this is OBSERVABILITY ONLY: we log the failure to Sentry — including
 * which page failed — and do not reload or retry. Recovery is intentionally
 * deferred until we can see, from these logs, how often it happens and where.
 */
const CHUNK_LOAD_ERROR_PATTERNS = [
  // Chromium
  /Failed to fetch dynamically imported module/i,
  // Firefox
  /error loading dynamically imported module/i,
  // Safari / WebKit
  /Importing a module script failed/i,
  // CSS code-split chunk (Vite's preload helper throws this verbatim)
  /Unable to preload CSS/i,
  // Legacy webpack-style messages, kept defensively
  /Loading( CSS)? chunk .* failed/i,
];

const isChunkLoadError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }

  if (error.name === 'ChunkLoadError') {
    return true;
  }

  return CHUNK_LOAD_ERROR_PATTERNS.some(pattern => pattern.test(error.message));
};

/**
 * The failed chunk's URL — present in Chromium/Firefox messages
 * ("…imported module: <url>") and CSS-preload messages; absent for Safari's
 * generic "Importing a module script failed.".
 */
const extractFailedChunk = (error: unknown): string | null => {
  if (!(error instanceof Error)) {
    return null;
  }

  const match = error.message.match(/https?:\/\/\S+?\.(?:m?js|css)\b/i);
  return match ? match[0] : null;
};

const routeName = (name: unknown): string =>
  typeof name === 'string' && name ? name : '(unnamed)';

/**
 * Log chunk-load failures that surface during navigation to Sentry, tagged
 * with the page being navigated to (and the matched route chain), the page
 * navigated from, and the failed chunk URL — so the report pinpoints exactly
 * which page's chunk failed and from where the user hit it.
 */
export const handleChunkLoadError = (router: Router): void => {
  router.onError((error, to, from) => {
    if (!isChunkLoadError(error)) {
      return;
    }

    sentryModel.logBaseError({
      error: error as Error,
      description: sentryModel.createCustomErrorText(
        sentryModel.errorPrefix.AVIA,
        'CHUNK_LOAD',
      ),
      level: sentryModel.level.ERROR,
      extra: {
        toRoute: routeName(to.name),
        toPath: to.fullPath,
        toMatched: to.matched.map(record => routeName(record.name)).join(' > '),
        fromRoute: routeName(from.name),
        fromPath: from.fullPath,
        failedChunk: extractFailedChunk(error) ?? 'unknown',
      },
    });
  });
};
